import { Colors } from 'core/CssVariables';

export type TThemeProps = {
  timesheet: {
    borderColor: string;
    backgroundColor: string;
  };
  avatar: {
    default: {
      color: string;
      backgroundColor: string;
    };
  };
  card: {
    default: {
      backgroundColor: string;
      border: string;
    };
    secondary: {
      backgroundColor: string;
    };
    primary: {
      backgroundColor: string;
    };
  };
  paragraph: {
    color: string;
    disableColor: string;
  };
  editableText: {
    spinner: string;
    color: string;
  };
  scrollbar: {
    track: string;
    thumb: string;
    modal: {
      thumb: string;
    };
  };
  color: string;
  secondaryColor: string;
  backButton: string;
  backgroundColor: string;
  secondaryBackgroundColor: string;
  radio: {
    label: string;
    backgroundColor: string;
  };
  input: {
    backgroundColor: string;
    placeholder: string;
    disabledPlaceholder: string;
    TextColorPrimary: string;
    borderColor: string;
  };
  sidebar: {
    backgroundColor: string;
    menuBackgroundColor: string;
    color: string;
    groupColor: string;
    groupLeadColor: string;
    iconColor: string;
    borderColor: string;
    hoverBackgroundColor: string;
    activeBackgroundColor: string;
    activeColor: string;
    activeIconColor: string;
    versionColor: string;
    logoColor: string;
    controlColor: string;
    activeRailColor: string;
  };
  dashbar: {
    backgroundColor: string;
    borderColor: string;
    labelColor: string;
    valueColor: string;
    iconColor: string;
    trackColor: string;
    infoColor: string;
    successColor: string;
    warningColor: string;
    criticalColor: string;
    ctaColor: string;
    avatarColor: string;
  };
  switch: {
    handle: string;
    themeSwitch: {
      backgroundColor: string;
      lightModeColor: string;
      darkModeColor: string;
    };
  };
  table: {
    backgroundColor: string;
    borderColor: string;
    hoverTextColor: string;
    sortersColor: string;
    pagesizeBackgroundColor: string;
    hoverRowBackgroundColor: string;
    checkboxBorderColor: string;
    checkboxLabelColor: string;
    filterBackgroundColor: string;
    textColor: string;
    expendedMenu: string;
    expendedMenuIcon: string;
  };
  datePicker: {
    backgroundColor: string;
    borderColor: string;
    disabledBackgroundColor: string;
  };
  timeEntryTooltip: {
    backgroundColor: string;
  };
  timeEntryDetails: {
    item: {
      color: string;
    };
  };
  modal: {
    backgroundColor: string;
    backgroundOpacity: string;
  };
  dropdown: {
    backgroundColor: string;
    borderColor: string;
    boxShadowColor: string;
  };
  tooltip: {
    backgroundColor: string;
    color: string;
  };
  icon: {
    color: string;
  };
  normalWrapper: {
    backgroundColor: string;
  };
  noResult: {
    color: string;
  };
  select: {
    color: string;
  };
  selectableText: {
    backgroundColor: string;
  };
  profileMenu: {
    color: string;
  };
  weekDays: {
    backgroundColor: string;
  };
  uploadButton: {
    backgroundColor: string;
    borderColor: string;
    iconColor: string;
  };
  progress: {
    backgroundColor: string;
  };
  text: {
    color: string;
  };
  fileUploader: {
    color: string;
    backgroundColor: string;
    borderDashed: string;
    border: string;
    actionIcons: string;
  };
  report: {
    backgroundColor: string;
    textColor: string;
    tooltip: {
      backgroundColor: string;
      borderColor: string;
    };
  };
  calendar: {
    backgroundColor: string;
    headerBackgroundColor: string;
  };
  announcement: {
    backgroundColor: string;
    color: string;
  };
  infoBanner: {
    backgroundColor: string;
    color: string;
  };
};

export const DarkTheme: TThemeProps = {
  timesheet: {
    borderColor: Colors.DarkBorderColor,
    backgroundColor: Colors.Dark,
  },
  avatar: {
    default: {
      color: Colors.Grey,
      backgroundColor: Colors.DarkGrey,
    },
  },
  card: {
    secondary: {
      backgroundColor: Colors.Dark,
    },
    primary: {
      backgroundColor: Colors.DarkGrey,
    },
    default: {
      backgroundColor: 'transparent',
      border: Colors.DarkBorderColor,
    },
  },
  paragraph: {
    color: Colors.LightGrey,
    disableColor: Colors.Grey,
  },
  editableText: {
    color: Colors.Dark,
    spinner: Colors.White,
  },
  scrollbar: {
    track: Colors.TextColorPrimary,
    thumb: Colors.Dark,
    modal: {
      thumb: Colors.Black,
    },
  },
  color: Colors.White,
  secondaryColor: Colors.LightGrey,
  backButton: Colors.Grey,
  switch: {
    handle: Colors.Dark,
    themeSwitch: {
      backgroundColor: Colors.PrimaryColor,
      lightModeColor: Colors.White,
      darkModeColor: Colors.PrimaryColor,
    },
  },
  backgroundColor: Colors.DarkGrey,
  secondaryBackgroundColor: Colors.Dark,
  radio: {
    label: Colors.LightGrey,
    backgroundColor: Colors.DarkGrey,
  },
  input: {
    backgroundColor: Colors.DarkBackground,
    placeholder: Colors.LightGrey,
    disabledPlaceholder: Colors.DisabledLightGrey,
    TextColorPrimary: Colors.LightGrey,
    borderColor: Colors.DarkBorderColor,
  },
  sidebar: {
    backgroundColor: Colors.ShellSurfaceDark,
    menuBackgroundColor: Colors.ShellSurfaceDark,
    color: Colors.ShellInkDark,
    groupColor: Colors.ShellGroupMutedDark,
    groupLeadColor: Colors.ShellGroupDark,
    iconColor: Colors.ShellIconDark,
    borderColor: Colors.ShellRuleDark,
    hoverBackgroundColor: Colors.ShellHoverDark,
    activeBackgroundColor: Colors.ShellActiveDark,
    activeColor: Colors.ShellInkDark,
    /* The dark design keeps the selected row's icon at the normal grey and
       gives it no accent rail — the #334155 fill on #1E293B is legible on its
       own, unlike light mode's 1.08:1 tint. */
    activeIconColor: Colors.ShellIconDark,
    activeRailColor: Colors.Transparent,
    versionColor: Colors.ShellGroupDark,
    logoColor: Colors.ShellInkDark,
    controlColor: Colors.ShellInkDark,
  },
  dashbar: {
    backgroundColor: Colors.ShellBarSurfaceDark,
    borderColor: Colors.ShellRuleDark,
    labelColor: Colors.ShellMutedDark,
    valueColor: Colors.ShellValueDark,
    iconColor: Colors.ShellIconDark,
    trackColor: Colors.ShellTrackDark,
    infoColor: Colors.ShellInfoDark,
    successColor: Colors.ShellSuccessDark,
    warningColor: Colors.ShellWarningDark,
    criticalColor: Colors.ShellCriticalDark,
    ctaColor: Colors.ShellCta,
    avatarColor: Colors.ShellAvatarDark,
  },
  table: {
    backgroundColor: Colors.DarkBackground,
    borderColor: Colors.DarkBorderColor,
    hoverTextColor: Colors.Dark,
    sortersColor: Colors.InfoColor,
    pagesizeBackgroundColor: Colors.Dark,
    hoverRowBackgroundColor: Colors.RowHover,
    checkboxBorderColor: Colors.LightGrey,
    checkboxLabelColor: Colors.LightGrey,
    filterBackgroundColor: Colors.DarkGrey,
    textColor: Colors.White,
    expendedMenu: Colors.White,
    expendedMenuIcon: Colors.Grey,
  },
  datePicker: {
    backgroundColor: Colors.DarkBackground,
    borderColor: Colors.DarkBorderColor,
    disabledBackgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  timeEntryTooltip: {
    backgroundColor: Colors.Dark,
  },
  timeEntryDetails: {
    item: {
      color: Colors.White,
    },
  },
  modal: {
    backgroundColor: Colors.Dark,
    backgroundOpacity: 'rgba(0, 0, 0, 0.7)',
  },
  dropdown: {
    backgroundColor: Colors.Dark,
    borderColor: Colors.DarkBorderColor,
    boxShadowColor: 'rgba(0, 0, 0, 0.3)',
  },
  tooltip: {
    backgroundColor: Colors.LightBackground,
    color: Colors.Grey,
  },
  icon: {
    color: Colors.DarkGrey,
  },
  normalWrapper: {
    backgroundColor: Colors.DarkBackground,
  },
  noResult: {
    color: Colors.LightGrey,
  },
  select: {
    color: Colors.LightGrey,
  },
  selectableText: {
    backgroundColor: Colors.DarkGrey,
  },
  profileMenu: {
    color: Colors.LightGrey,
  },
  weekDays: {
    backgroundColor: Colors.DarkBackground,
  },
  uploadButton: {
    backgroundColor: Colors.Dark,
    borderColor: Colors.DarkBorderColor,
    iconColor: Colors.Grey,
  },
  progress: {
    backgroundColor: Colors.DarkGrey,
  },
  text: {
    color: Colors.LightGrey,
  },
  fileUploader: {
    color: Colors.LightGrey,
    backgroundColor: Colors.Dark,
    borderDashed: Colors.Grey,
    border: Colors.DarkBorderColor,
    actionIcons: Colors.LightGrey,
  },
  report: {
    backgroundColor: Colors.Dark,
    textColor: Colors.White,
    tooltip: {
      backgroundColor: Colors.DarkBackground,
      borderColor: Colors.DarkBorderColor,
    },
  },
  calendar: {
    backgroundColor: Colors.DarkGrey,
    headerBackgroundColor: Colors.DarkBackground,
  },
  /* Dark mode inverts the light theme's navy band: an off-white panel with
     dark text, so the announcement stands off the dark shell instead of
     sinking into it (PM request on ZEST-800). */
  /* Both banners take the light theme's info tint as a SOLID: rgba(0,120,249,
     0.1) over white composites to #E6F2FE. Used flat rather than as alpha,
     which over the dark shell would come out dark instead. */
  announcement: {
    backgroundColor: '#E6F2FE',
    color: Colors.TextColorPrimary,
  },
  infoBanner: {
    backgroundColor: '#E6F2FE',
    color: Colors.TextColorPrimary,
  },
};

export const LightTheme: TThemeProps = {
  timesheet: {
    borderColor: Colors.LightBorderColor,
    backgroundColor: Colors.LightBackground,
  },
  avatar: {
    default: {
      color: Colors.LightGrey,
      backgroundColor: Colors.LightInput,
    },
  },
  card: {
    secondary: {
      backgroundColor: Colors.LightBackground,
    },
    primary: {
      backgroundColor: Colors.White,
    },
    default: {
      backgroundColor: 'transparent',
      border: Colors.LightBorderColor,
    },
  },
  paragraph: {
    color: Colors.Grey,
    disableColor: Colors.LightGrey,
  },
  editableText: {
    spinner: Colors.InfoColor,
    color: Colors.Dark,
  },
  scrollbar: {
    thumb: Colors.LightGrey,
    track: Colors.LightBorderColor,
    modal: {
      thumb: Colors.LightGrey,
    },
  },
  color: Colors.Dark,
  secondaryColor: Colors.Grey,
  backButton: Colors.Dark,
  switch: {
    handle: Colors.White,
    themeSwitch: {
      backgroundColor: Colors.InfoColor,
      lightModeColor: Colors.InfoColor,
      darkModeColor: Colors.Dark,
    },
  },
  backgroundColor: Colors.White,
  secondaryBackgroundColor: Colors.LightBackground,
  radio: {
    label: Colors.DarkGrey,
    backgroundColor: Colors.White,
  },
  input: {
    backgroundColor: Colors.LightInput,
    placeholder: Colors.Grey,
    disabledPlaceholder: Colors.DisabledLightGrey,
    TextColorPrimary: Colors.TextColorPrimary,
    borderColor: Colors.LightBorderColor,
  },
  sidebar: {
    backgroundColor: Colors.ShellSurface,
    menuBackgroundColor: Colors.ShellSurface,
    color: Colors.ShellInk,
    groupColor: Colors.ShellIcon,
    groupLeadColor: Colors.ShellMuted,
    iconColor: Colors.ShellIcon,
    borderColor: Colors.ShellTrack,
    hoverBackgroundColor: Colors.ShellHover,
    activeBackgroundColor: Colors.ShellActive,
    activeColor: Colors.ShellInk,
    activeIconColor: Colors.ShellAccent,
    activeRailColor: Colors.ShellAccent,
    versionColor: Colors.ShellMuted,
    logoColor: Colors.ShellInk,
    controlColor: Colors.ShellAvatar,
  },
  dashbar: {
    backgroundColor: Colors.ShellBarSurface,
    borderColor: Colors.ShellRule,
    labelColor: Colors.ShellMuted,
    valueColor: Colors.ShellInk,
    iconColor: Colors.ShellIcon,
    trackColor: Colors.ShellTrack,
    infoColor: Colors.ShellInfo,
    successColor: Colors.ShellSuccess,
    warningColor: Colors.ShellWarning,
    criticalColor: Colors.ShellCritical,
    ctaColor: Colors.ShellCta,
    avatarColor: Colors.ShellAvatar,
  },
  table: {
    backgroundColor: Colors.White,
    borderColor: Colors.LightBorderColor,
    hoverTextColor: Colors.Dark,
    sortersColor: Colors.InfoColor,
    pagesizeBackgroundColor: Colors.LightBackground,
    hoverRowBackgroundColor: Colors.LightTableBackground,
    checkboxBorderColor: Colors.Grey,
    checkboxLabelColor: Colors.DarkBorderColor,
    filterBackgroundColor: Colors.White,
    textColor: Colors.TextColorPrimary,
    expendedMenu: Colors.Grey,
    expendedMenuIcon: Colors.White,
  },
  datePicker: {
    backgroundColor: Colors.LightInput,
    borderColor: Colors.LightBorderColor,
    disabledBackgroundColor: 'rgba(0, 0, 0, 0.06)',
  },
  timeEntryTooltip: {
    backgroundColor: Colors.White,
  },
  timeEntryDetails: {
    item: {
      color: Colors.Grey,
    },
  },
  modal: {
    backgroundColor: Colors.White,
    backgroundOpacity: 'rgba(0, 0, 0, 0.2)',
  },
  dropdown: {
    backgroundColor: Colors.White,
    borderColor: Colors.LightBorderColor,
    boxShadowColor: 'rgba(81, 81, 81, 0.12)',
  },
  tooltip: {
    backgroundColor: Colors.DarkBorderColor,
    color: Colors.White,
  },
  icon: {
    color: Colors.LightGrey,
  },
  normalWrapper: {
    backgroundColor: Colors.LightBackground,
  },
  noResult: {
    color: Colors.Grey,
  },
  select: {
    color: Colors.TextColorPrimary,
  },
  selectableText: {
    backgroundColor: Colors.LightInput,
  },
  profileMenu: {
    color: Colors.Grey,
  },
  weekDays: {
    backgroundColor: Colors.LightBorderColor,
  },
  uploadButton: {
    backgroundColor: Colors.LightBackground,
    borderColor: Colors.LightBorderColor,
    iconColor: Colors.LightGrey,
  },
  progress: {
    backgroundColor: Colors.LightGrey,
  },
  text: {
    color: Colors.Grey,
  },
  fileUploader: {
    color: Colors.Grey,
    backgroundColor: Colors.LightInput,
    borderDashed: Colors.LightGrey,
    border: Colors.LightBorderColor,
    actionIcons: Colors.LightInput,
  },
  report: {
    backgroundColor: Colors.LightBackground,
    textColor: Colors.Dark,
    tooltip: {
      backgroundColor: Colors.LightInput,
      borderColor: Colors.LightBorderColor,
    },
  },
  calendar: {
    backgroundColor: Colors.LightBackground,
    headerBackgroundColor: Colors.LightInput,
  },
  announcement: {
    backgroundColor: Colors.ShellAvatar,
    color: Colors.White,
  },
  /* The timezone prompt takes the announcement's navy band so the two banners
     match in light mode too. */
  infoBanner: {
    backgroundColor: Colors.ShellAvatar,
    color: Colors.White,
  },
};
